import { _MiscMessages, ConfigSystemParams, MdmDeviceClass, MobileOperatingSystems, WidgetTitles } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ComplianceAndPercentageDTO, ComplianceAndPercentageDTO_EmptyObject } from 'src/app/core/interfaces/general/compliance-and-percentage';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetProgress } from 'src/app/core/interfaces/general/widget-progress';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

const compliant   ="Compliant";
const noncompliant="Noncompliant";
 
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-mdm-device-compliance-02',
  templateUrl: './widget-row-mdm-device-compliance-02.component.html',
  styleUrls: ['./widget-row-mdm-device-compliance-02.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowMdmDeviceCompliance02Component implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() previousSync       : boolean | undefined=false;
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();

  get isOtherClass() { return this.deviceClass===MdmDeviceClass.other; }
    
  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_Compliance_MdmDevices02, false); }

  private filterCompanyID              : number | undefined;
  private filterDate                   : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statSubTitleComplianceAndroid: string="Software Compliance";
  private statSubTitleComplianceIos    : string="Software Compliance";
  private statSubTitleJailbrokenDevices: string="Devices";
  private statSubTitlePinCodeCompliance: string="Requirements";
  private statTitleComplianceAndroid   : string=WidgetTitles.mdmDeviceComplianceSoftwareAndroid;
  private statTitleComplianceIos       : string=WidgetTitles.mdmDeviceComplianceSoftwareIos;
  private statTitleJailbrokenDevices   : string=WidgetTitles.mdmDeviceComplianceJailbroken;
  private statTitlePinCodeCompliance   : string=WidgetTitles.mdmDeviceCompliancePinCode;
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number }; refreshToken?: number; }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<WidgetProgress>;
  public visibleContainer: boolean=true;

  public readonly mobileOnlyTitles: string[]=[WidgetTitles.mdmDeviceComplianceSoftwareAndroid, WidgetTitles.mdmDeviceComplianceSoftwareIos, WidgetTitles.mdmDeviceComplianceJailbroken];
  
  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['deviceClass'] && !changes['deviceClass'].firstChange && (this.filterCompanyID ?? -1)!=-1)
        this.refreshWidgets();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshWidgets(): void {
    try {
      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
  
      this.selection$.next({ companyId, date, refreshToken: Date.now() });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statComplianceAndroid: WidgetProgress={
        accentColour: "purple",
        icon        : "bx bxl-android",
        id      : 1,
        title   : this.statTitleComplianceAndroid,
        subTitle: this.statSubTitleComplianceAndroid,
        subItem : [
          {
            id       : 1,
            icon     : "mdi mdi-numeric-1-circle",
            iconClass: "success",
            label    : compliant,
          },
          {
            id       : 2,
            icon     : "mdi mdi-numeric-2-circle",
            iconClass: "danger",
            label    : noncompliant,
          },
        ],
        progressBar: [
          { id: 1, bgColor: "bg-success", width: "50" },
          { id: 2, bgColor: "bg-danger",  width: "50" },
        ],
      }
                   
      let statComplianceIos: WidgetProgress={
        accentColour: "info",
        icon        : "bx bxl-apple",
        id      : 2,
        title   : this.statTitleComplianceIos,
        subTitle: this.statSubTitleComplianceIos,
        subItem : [
          {
            id       : 1,
            icon     : "mdi mdi-numeric-1-circle",
            iconClass: "success",
            label    : compliant,
          },
          {
            id       : 2,
            icon     : "mdi mdi-numeric-2-circle",
            iconClass: "danger",
            label    : noncompliant,
          },
        ],
        progressBar: [
            { id: 1, bgColor: "bg-success", width: "50" },
            { id: 2, bgColor: "bg-danger",  width: "50" },
        ],
      }
                   
      let statJailbrokenDevices: WidgetProgress={
        accentColour: "danger",
        icon        : "bx bx-lock-open-alt",
        id      : 3,
        title   : this.statTitleJailbrokenDevices,
        subTitle: this.statSubTitleJailbrokenDevices,
        subItem : [
          {
            id       : 1,
            icon     : "mdi mdi-numeric-1-circle",
            iconClass: "success",
            label    : compliant,
          },
          {
            id       : 2,
            icon     : "mdi mdi-numeric-2-circle",
            iconClass: "danger",
            label    : noncompliant,
          },
        ],
        progressBar: [
            { id: 1, bgColor: "bg-success", width: "50" },
            { id: 2, bgColor: "bg-danger",  width: "50" },
        ],
      }
                   
      let statPinCodeCompliance: WidgetProgress={
        accentColour: "success",
        icon        : "bx bx-lock-alt",
        id      : 4,
        title   : this.statTitlePinCodeCompliance,
        subTitle: this.statSubTitlePinCodeCompliance,
        subItem : [
         {
           id       : 1,
           icon     : "mdi mdi-numeric-1-circle",
           iconClass: "success",
           label    : compliant,
         },
         {
           id       : 2,
           icon     : "mdi mdi-numeric-2-circle",
           iconClass: "danger",
           label    : noncompliant,
         },
        ],
        progressBar: [
            { id: 1, bgColor: "bg-success", width: "50" },
            { id: 2, bgColor: "bg-danger",  width: "50" },
        ],
      }
                
      this.statData.push(statComplianceAndroid);
      this.statData.push(statComplianceIos);
      this.statData.push(statPinCodeCompliance);
      this.statData.push(statJailbrokenDevices);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId   ===b.companyId  &&
          a.date.year   ===b.date.year  &&
          a.date.month  ===b.date.month &&
          a.date.day    ===b.date.day   &&
          a.refreshToken===b.refreshToken
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
                    
          const complianceAndroidSource=this.pollingWidgets
                                         ? this.widgetService.getCompliance_SoftwareUpdates(companyId, MobileOperatingSystems.android, this.deviceClass)
                                         : this.widgetService.getCompliance_Software       (companyId, MobileOperatingSystems.android, this.deviceClass);

          const complianceIosSource=this.pollingWidgets
                                    ? this.widgetService.getCompliance_SoftwareUpdates(companyId, MobileOperatingSystems.iOS, this.deviceClass)
                                    : this.widgetService.getCompliance_Software       (companyId, MobileOperatingSystems.iOS, this.deviceClass);

          const complianceJailbrokenSource=this.pollingWidgets
                                           ? this.widgetService.getCompliance_JailbrokenUpdates(companyId, this.deviceClass)
                                           : this.widgetService.getCompliance_Jailbroken       (companyId, this.deviceClass);

          const compliancePinCodeSource=this.pollingWidgets
                                        ? this.widgetService.getCompliance_PinCodeUpdates(companyId, this.deviceClass)
                                        : this.widgetService.getCompliance_PinCode       (companyId, this.deviceClass);

          return combineLatest({
            complianceAndroid   : safeStart(complianceAndroidSource,    ComplianceAndPercentageDTO_EmptyObject()),
            complianceIos       : safeStart(complianceIosSource,        ComplianceAndPercentageDTO_EmptyObject()),
            complianceJailbroken: safeStart(complianceJailbrokenSource, ComplianceAndPercentageDTO_EmptyObject()),
            compliancePinCode   : safeStart(compliancePinCodeSource,    ComplianceAndPercentageDTO_EmptyObject()),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20260209 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyComplianceAndroid   (result.complianceAndroid);
        this.applyComplianceIos       (result.complianceIos);
        this.applyComplianceJailbroken(result.complianceJailbroken);
        this.applyCompliancePinCode   (result.compliancePinCode);
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyComplianceAndroid(data: ComplianceAndPercentageDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetProgress) => {
        if (stat.title==this.statTitleComplianceAndroid) {
        stat.value   =data.compliantPercent,
        stat.subItem =[
            {
              icon     : "mdi mdi-numeric-1-circle",
              iconClass: "success",
              label    : compliant + ` ${data.compliant}`
            },
            {
              icon     : "mdi mdi-numeric-2-circle",
              iconClass: "danger",
              label    : noncompliant + ` ${data.notCompliant}`
            },
        ],
          stat.progressBar=[
              { bgColor: "bg-success", width: data.compliantPercent    },
              { bgColor: "bg-danger",  width: data.notCompliantPercent },
          ]
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyComplianceIos(data: ComplianceAndPercentageDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetProgress) => {
        if (stat.title==this.statTitleComplianceIos) {
        stat.value   =data.compliantPercent,
        stat.subItem =[
            {
              icon     : "mdi mdi-numeric-1-circle",
              iconClass: "success",
              label    : compliant + ` ${data.compliant}`
            },
            {
              icon     : "mdi mdi-numeric-2-circle",
              iconClass: "danger",
              label    : noncompliant + ` ${data.notCompliant}`
            },
        ],
          stat.progressBar=[
              { bgColor: "bg-success", width: data.compliantPercent    },
              { bgColor: "bg-danger",  width: data.notCompliantPercent },
          ]
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyComplianceJailbroken(data: ComplianceAndPercentageDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetProgress) => {
        if (stat.title==this.statTitleJailbrokenDevices) {
        stat.value   =data.notCompliantPercent,
        stat.subItem =[
            {
              icon     : "mdi mdi-numeric-1-circle",
              iconClass: "success",
              label    : compliant + ` ${data.compliant}`
            },
            {
              icon     : "mdi mdi-numeric-2-circle",
              iconClass: "danger",
              label    : noncompliant + ` ${data.notCompliant}`
            },
        ],
          stat.progressBar=[
              { bgColor: "bg-success", width: data.compliantPercent    },
              { bgColor: "bg-danger",  width: data.notCompliantPercent },
          ];

          stat.accentColour=(Number(data.notCompliant) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyCompliancePinCode(data: ComplianceAndPercentageDTO) {
    try {
      if (!this.statData)
        this.displayData();

      this.statData.forEach((stat: WidgetProgress) => {
        if (stat.title==this.statTitlePinCodeCompliance) {
        stat.value   =data.compliantPercent,
        stat.subItem =[
            {
              icon     : "mdi mdi-numeric-1-circle",
              iconClass: "success",
              label    : compliant + ` ${data.compliant}`
            },
            {
              icon     : "mdi mdi-numeric-2-circle",
              iconClass: "danger",
              label    : noncompliant + ` ${data.notCompliant}`
            },
        ],
          stat.progressBar=[
              { bgColor: "bg-success", width: data.compliantPercent    },
              { bgColor: "bg-danger",  width: data.notCompliantPercent },
          ];

          stat.accentColour=(Number(data.notCompliant) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
